import {Box, DialogContent, DialogTitle, Tab, Tabs} from '@mui/material';
import {Suspense, useState} from 'react';
import {CloseButton} from '../common/button/CloseButton';
import {ErrorBoundary} from '../common/ErrorBoundary';
import {Loading} from '../common/Loading';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {Translation} from '../i18n/Translation';
import {ComponentWithoutChildren} from '../react/types';
import {ResponsiveDialog, ResponsiveDialogProps} from '../responsive/ResponsiveDialog';
import {AddSignupRuleForm} from './AddSignupRuleForm';
import {SignupRuleList} from './SignupRuleList';
import {WaitlistList} from './WaitlistList';
import {User} from '#common/users/types';

type AllowedDialogProps = Omit<ResponsiveDialogProps, 'onClose'>;
export interface AdministrationDialogProps extends AllowedDialogProps {
  handleClose: () => void;
  user: User;
}

export const AdministrationDialog: ComponentWithoutChildren<AdministrationDialogProps> = ({
  handleClose,
  open,
  user,
  ...dialogProps
}: AdministrationDialogProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const {translate} = useTranslation('user');

  return (
    <ResponsiveDialog {...dialogProps} fullWidth maxWidth="lg" onClose={handleClose} open={open}>
      <DialogTitle>
        <Translation keys="user:administration">{(text) => <span>{text}</span>}</Translation>
        <CloseButton onClick={handleClose} sx={{position: 'absolute', right: 8, top: 8}} />
      </DialogTitle>
      <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs onChange={(_, value: number) => setActiveTab(value)} value={activeTab}>
          <Tab label={translate('user:administration.tabs.whitelist')} />
          <Tab label={translate('user:administration.tabs.blacklist')} />
          <Tab label={translate('user:administration.tabs.waitlist')} />
        </Tabs>
      </Box>
      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 400,
          p: 0,
        }}
      >
        <ErrorBoundary context="Administration">
          <Suspense
            fallback={
              <Box sx={{alignItems: 'center', display: 'flex', flexGrow: 1, justifyContent: 'center'}}>
                <Loading />
              </Box>
            }
          >
            {open && (
              <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 1}}>
                {activeTab === 0 && (
                  <>
                    <AddSignupRuleForm type="whitelist" user={user} />
                    <SignupRuleList type="whitelist" user={user} />
                  </>
                )}
                {activeTab === 1 && (
                  <>
                    <AddSignupRuleForm type="blacklist" user={user} />
                    <SignupRuleList type="blacklist" user={user} />
                  </>
                )}
                {activeTab === 2 && <WaitlistList user={user} />}
              </Box>
            )}
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </ResponsiveDialog>
  );
};

export default AdministrationDialog;
